// src/components/ContactMerge.js — review possible duplicates and merge two contacts
// [marker: contact-merge v1] [marker: invoice-links v1]
// <DuplicatesModal onClose onChanged />   list of likely duplicates → Review → MergeModal
// <MergeModal ids={[a,b]} onClose onMerged />  pick the record to keep and which value wins per field
// <MergeWithPicker contact onClose onMerged />  start a merge from a contact page
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const LABELS = {
  first_name: 'First name', last_name: 'Last name', salutation: 'Salutation', title: 'Title', email: 'Email',
  secondary_email: 'Second email', phone: 'Phone', secondary_phone: 'Second phone', business_id: 'Company',
  company_name: 'Company name (text)', office_or_org: 'Office / organization', government_level: 'Government level',
  contact_type: 'Contact type', contact_frequency: 'Contact frequency', birthday: 'Birthday', address_line1: 'Address',
  address_line2: 'Address line 2', city: 'City', state: 'State', zip_code: 'ZIP', vip: 'VIP', association: 'Association',
  photo_filename: 'Photo', photo_original_name: 'Photo name', email_blast_opt_in: 'Email blasts',
};
const LINK_LABELS = {
  tickets: 'tickets', invoices: 'invoices', sponsorships: 'sponsorships', received_payments: 'payments',
  letters: 'letters', automated_email_log: 'emails', email_blast_recipients: 'blasts', renewal_batch_items: 'renewals',
  contact_notes: 'notes', tasks: 'tasks', tags: 'tags',
};
const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const show = (c, f) => {
  if (f === 'business_id') return c.business_name || (c.business_id ? `#${c.business_id}` : '');
  if (f === 'email_blast_opt_in') return c[f] === null || c[f] === undefined ? '' : (c[f] ? 'Yes' : 'No');
  if (f === 'photo_filename') return c[f] ? 'has photo' : '';
  return c[f] === null || c[f] === undefined ? '' : String(c[f]);
};
const name = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Contact #${c.id}`;

export function DuplicatesModal({ onClose, onChanged }) {
  const [groups, setGroups] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const load = () => api.get('/contact-merge/duplicates').then(r => setGroups(r.data || [])).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);
  const dismiss = async (g) => {
    try { await api.post('/contact-merge/dismiss', { ids: g.contacts.map(c => c.id) }); toast.success('Marked as different people.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <span className="modal-title">Possible Duplicate Contacts</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {groups === null ? <div className="spinner" /> : groups.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">✅</div><h3>No duplicates found</h3></div>
          ) : (
            <>
              <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                {groups.length} group{groups.length === 1 ? '' : 's'} share an email, a full name, or a phone number and last name. Review each one: merge them, or mark them as different people so they stop showing here.
              </p>
              {groups.map((g, i) => (
                <div key={i} style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase' }}>{g.reasons.join(' · ')}</span>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => setReviewing(g.contacts.slice(0, 2).map(c => c.id))}>Review &amp; merge</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => dismiss(g)}>Not duplicates</button>
                    </span>
                  </div>
                  <table style={{ width: '100%', fontSize: '0.85rem', marginTop: 4 }}>
                    <tbody>
                      {g.contacts.map(c => (
                        <tr key={c.id}>
                          <td style={{ padding: '2px 6px' }}><a href={`/contacts/${c.id}`} target="_blank" rel="noreferrer"><strong>{name(c)}</strong></a></td>
                          <td style={{ padding: '2px 6px' }}>{c.email || ''}</td>
                          <td style={{ padding: '2px 6px' }}>{c.phone || ''}</td>
                          <td style={{ padding: '2px 6px', color: 'var(--gray-500)' }}>{c.business_name || ''}</td>
                          <td style={{ padding: '2px 6px', color: 'var(--gray-400)' }}>added {c.created}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {g.contacts.length > 2 && <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Three or more: merge two at a time — the rest stay listed until done.</div>}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
      {reviewing && (
        <MergeModal ids={reviewing} onClose={() => setReviewing(null)}
                    onMerged={() => { setReviewing(null); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

export function MergeModal({ ids, onClose, onMerged }) {
  const [data, setData] = useState(null);
  const [keepIdx, setKeepIdx] = useState(0);
  const [pick, setPick] = useState({}); // field -> 0 | 1 (which column wins)
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/contact-merge/compare?ids=${ids.join(',')}`).then(r => {
      setData(r.data);
      // keep the record with more history by default
      const score = (c) => Object.values(c.linked || {}).reduce((a, b) => a + b, 0);
      const k = score(r.data.contacts[1]) > score(r.data.contacts[0]) ? 1 : 0;
      setKeepIdx(k);
      const p = {};
      r.data.fields.forEach(f => {
        const a = show(r.data.contacts[k], f), b = show(r.data.contacts[1 - k], f);
        p[f] = !blank(a) ? k : (!blank(b) ? 1 - k : k);
      });
      setPick(p);
    }).catch(err => { toast.error(err.response?.data?.error || 'Could not load the contacts.'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <div className="modal-overlay"><div className="modal"><div className="modal-body"><div className="spinner" /></div></div></div>;
  const [c0, c1] = data.contacts;
  const conflicts = data.fields.filter(f => show(c0, f) !== show(c1, f) && !(blank(show(c0, f)) && blank(show(c1, f))));
  const keep = data.contacts[keepIdx]; const other = data.contacts[1 - keepIdx];

  const merge = async () => {
    if (!window.confirm(`Merge "${name(other)}" into "${name(keep)}"? Everything linked to it moves over and it is removed. This can't be undone.`)) return;
    const fields = {};
    data.fields.forEach(f => { fields[f] = pick[f] === keepIdx ? 'keep' : 'merge'; });
    setSaving(true);
    try {
      const r = await api.post('/contact-merge/merge', { keepId: keep.id, mergeId: other.id, fields });
      toast.success(r.data.message || 'Merged.');
      onMerged && onMerged(keep.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not merge.'); }
    finally { setSaving(false); }
  };

  const linkedText = (c) => Object.entries(c.linked || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${LINK_LABELS[k] || k}`).join(', ') || 'nothing linked';

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1600 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <span className="modal-title">Merge Contacts</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 6, width: 170 }}></th>
                {[c0, c1].map((c, i) => (
                  <th key={c.id} style={{ textAlign: 'left', padding: 6, background: keepIdx === i ? '#ecfdf5' : 'var(--gray-50)' }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                      <input type="radio" checked={keepIdx === i} onChange={() => setKeepIdx(i)} />
                      {keepIdx === i ? 'KEEP this record' : 'Merge into the other'}
                    </label>
                    <div style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 2 }}>
                      #{c.id} · {linkedText(c)}{c.has_portal_login ? ' · has portal login' : ''}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {conflicts.length === 0 && <tr><td colSpan={3} style={{ padding: 10, color: 'var(--gray-500)' }}>The details match — nothing to choose.</td></tr>}
              {conflicts.map(f => (
                <tr key={f} style={{ borderTop: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{LABELS[f] || f}</td>
                  {[c0, c1].map((c, i) => (
                    <td key={c.id} style={{ padding: 6, background: pick[f] === i ? '#eff6ff' : undefined }}>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" checked={pick[f] === i} onChange={() => setPick(p => ({ ...p, [f]: i }))} />
                        {blank(show(c, f)) ? <span style={{ color: 'var(--gray-400)' }}>(empty)</span> : show(c, f)}
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="form-hint" style={{ marginTop: 10 }}>
            Everything linked to the other record (tickets, invoices, sponsorships, payments, notes, tasks, tags, email history) moves to the record you keep.
            An email address that isn’t chosen is saved as the second email when that’s free.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={merge} disabled={saving}>{saving ? 'Merging…' : `Merge into ${name(keep)}`}</button>
        </div>
      </div>
    </div>
  );
}

export function MergeWithPicker({ contact, onClose, onMerged }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [chosen, setChosen] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setList([]); return; }
    const t = setTimeout(() => {
      api.get(`/contacts?search=${encodeURIComponent(q.trim())}&limit=10`)
        .then(r => setList((r.data?.data || []).filter(c => c.id !== contact.id))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, contact.id]);
  if (chosen) return <MergeModal ids={[contact.id, chosen]} onClose={onClose} onMerged={onMerged} />;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Merge {name(contact)} with…</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <input className="form-input" autoFocus placeholder="Search name or email" value={q} onChange={e => setQ(e.target.value)} />
          <div style={{ marginTop: 8 }}>
            {list.map(c => (
              <div key={c.id} onClick={() => setChosen(c.id)} style={{ padding: '7px 8px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                <strong>{name(c)}</strong> <span style={{ color: 'var(--gray-500)' }}>{c.email || ''} {c.business_name ? `· ${c.business_name}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
